import { flexRender } from '@tanstack/react-table'
import type { CSSProperties } from 'react'

import type { Header } from '@/components/ui/table/types'
import { cn } from '@/utils'
import { DataTableHeaderSort } from './DataTableHeaderSort'
import type { TableData } from './types'

interface DataTableHeaderProps<TData extends TableData> {
  header: Header<TData, unknown>
}

export const DataTableHeader = function DataTable<TData extends TableData>({
  header,
}: DataTableHeaderProps<TData>) {
  const canSort = header.column.getCanSort()
  const { isFluid } = header.column.columnDef.meta || {}
  const style = {
    '--header-size': `${header.getSize()}px`,
  } as CSSProperties

  const content = (
    <>
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}

      <DataTableHeaderSort header={header} />
    </>
  )
  const contentClassName =
    'flex-1 h-full truncate flex items-center gap-1.5 text-left font-normal'

  // Only sortable headers are buttons, so a header holding its own control (checkbox, star) is never nested in one.
  return (
    <div
      key={header.id}
      className={cn(
        'relative h-10 flex items-center text-left font-normal text-blue-7 text-sm px-2 first:pl-0 shrink-0 group border-r last:border-r-0 border-border select-none',
        canSort && 'cursor-pointer hover:bg-neutral-50 active:bg-neutral-100',
        isFluid && 'flex-1 w-auto',
        !isFluid && 'w-(--header-size)',
      )}
      style={style}
    >
      {canSort ? (
        <button
          type="button"
          className={cn(contentClassName, 'cursor-pointer')}
          onClick={header.column.getToggleSortingHandler()}
        >
          {content}
        </button>
      ) : (
        <div className={contentClassName}>{content}</div>
      )}

      <div
        aria-hidden="true"
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none translate-x-1/2 z-10',
          'after:absolute after:inset-y-0 after:right-1/2 after:translate-x-1/2 after:w-px after:transition-colors',
          header.column.getIsResizing()
            ? 'after:bg-brand after:w-0.5'
            : 'after:hover:bg-border',
        )}
      />
    </div>
  )
}
